import { useState } from 'react'
import {
  useAddTaskWorkLog,
  useChangeTaskMissingItems,
  useSetTaskProgress,
  useTaskMissingItems,
  useTaskWorkLog
} from '../api/queries'
import type { Task, TaskMissingItem, TaskMissingItemStatus } from '../api/types'
import { formatDate, formatDateTime } from '../lib/format'

const statusStyle: Record<TaskMissingItemStatus, React.CSSProperties> = {
  missing: { background: '#FEE2E2', color: '#991B1B' },
  ordered: { background: '#FEF3C7', color: '#92400E' },
  ready: { background: '#DCFCE7', color: '#166534' }
}

/**
 * Opgavens forløb (beslutning 085): hvor langt den er, hvad der er lavet, og hvad der
 * mangler - og det, der er bestilt.
 *
 * Tænkt til montøren, der har været ude uden at blive færdig: han sætter procenten,
 * skriver hvad han har lavet, og skriver det ned, der står i vejen. Listerne hentes
 * først, når panelet åbnes - en opgaveliste skal ikke hente tyve dagbøger.
 */
export function TaskWorkPanel({ task }: { task: Task }) {
  const [open, setOpen] = useState(false)
  const isDone = task.status.isCompleted
  const isCancelled = task.status.isCancelled

  const summary = [
    task.workLogCount > 0 && `${task.workLogCount} ${task.workLogCount === 1 ? 'indskrivning' : 'indskrivninger'}`,
    task.openMissingCount > 0 && `${task.openMissingCount} mangler`,
    task.orderedCount > 0 && `heraf ${task.orderedCount} bestilt`
  ].filter(Boolean)

  return (
    <div style={{ marginTop: 10 }}>
      {/* Fremdriften vises på kortet, så man kan se den uden at åbne noget. */}
      {!isDone && !isCancelled && task.progressPercent > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div className="row row--between tiny muted" style={{ marginBottom: 3 }}>
            <span>Fremdrift</span>
            <span>{task.progressPercent} %</span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
            <div style={{ width: `${task.progressPercent}%`, height: '100%', background: 'var(--primary, #2563EB)' }} />
          </div>
        </div>
      )}

      <button type="button" className="btn btn--sm" onClick={() => setOpen((value) => !value)}>
        {open ? 'Luk forløb' : 'Fremdrift, udført og mangler'}
        {!open && summary.length > 0 && <span className="tiny muted"> · {summary.join(' · ')}</span>}
      </button>

      {open && (
        <div className="stack" style={{ gap: 14, marginTop: 12 }}>
          {!isDone && !isCancelled && <ProgressEditor task={task} />}
          <WorkLogSection task={task} />
          <MissingItemsSection task={task} />
        </div>
      )}
    </div>
  )
}

/** Procenten i spring af 10 - finere end det kan ingen skønne ude på en sag. */
function ProgressEditor({ task }: { task: Task }) {
  const setProgress = useSetTaskProgress(task.id)
  const [value, setValue] = useState(task.progressPercent)
  const [error, setError] = useState<string | null>(null)

  async function save() {
    setError(null)
    try {
      await setProgress.mutateAsync(value)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fremdriften kunne ikke gemmes.')
    }
  }

  return (
    <div>
      <div className="small" style={{ fontWeight: 600, marginBottom: 6 }}>
        Hvor langt er opgaven?
      </div>
      <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="range"
          min={0}
          max={100}
          step={10}
          value={value}
          onChange={(e) => setValue(Number(e.target.value))}
          style={{ flex: '1 1 180px', maxWidth: 320 }}
          aria-label="Fremdrift i procent"
        />
        <span className="small" style={{ minWidth: 44 }}>
          {value} %
        </span>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={setProgress.isPending || value === task.progressPercent}
          onClick={() => void save()}
        >
          {setProgress.isPending ? 'Gemmer ...' : 'Gem'}
        </button>
      </div>
      <div className="tiny muted" style={{ marginTop: 4 }}>
        Tæller med i sagens fremdrift: en opgave på 50 % tæller halvt. Marker den færdig, når den er det.
      </div>
      {error && <div className="tiny warn">{error}</div>}
    </div>
  )
}

/** Loggen over udført arbejde - nyeste først, med hvem og hvornår. */
function WorkLogSection({ task }: { task: Task }) {
  const { data: entries, isLoading } = useTaskWorkLog(task.id)
  const add = useAddTaskWorkLog(task.id)
  const [body, setBody] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      await add.mutateAsync({ body })
      setBody('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Det udførte arbejde kunne ikke gemmes.')
    }
  }

  return (
    <div>
      <div className="small" style={{ fontWeight: 600, marginBottom: 6 }}>
        Udført arbejde
      </div>

      <form onSubmit={(e) => void submit(e)}>
        <textarea
          rows={3}
          maxLength={4000}
          placeholder="Hvad har du lavet på opgaven i dag?"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className="row" style={{ gap: 8, marginTop: 6 }}>
          <button className="btn btn--primary btn--sm" type="submit" disabled={add.isPending || !body.trim()}>
            {add.isPending ? 'Gemmer ...' : 'Skriv i loggen'}
          </button>
          {error && <span className="tiny warn">{error}</span>}
        </div>
      </form>

      {isLoading && <div className="tiny muted" style={{ marginTop: 8 }}>Henter ...</div>}
      {entries && entries.length === 0 && (
        <div className="tiny muted" style={{ marginTop: 8 }}>
          Der er ikke skrevet noget endnu.
        </div>
      )}
      {entries && entries.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0' }}>
          {entries.map((entry) => (
            <li key={entry.id} style={{ borderLeft: '3px solid var(--border)', paddingLeft: 10, marginBottom: 10 }}>
              <div className="tiny muted">
                {formatDateTime(entry.createdAt)} · {entry.user.displayName}
              </div>
              <div className="small" style={{ whiteSpace: 'pre-wrap' }}>
                {entry.body}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Det, der mangler for at blive færdig - med status Mangler, Bestilt eller Klar. */
function MissingItemsSection({ task }: { task: Task }) {
  const { data: items, isLoading } = useTaskMissingItems(task.id)
  const change = useChangeTaskMissingItems(task.id)
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function run(action: Parameters<typeof change.mutateAsync>[0]) {
    setError(null)
    try {
      await change.mutateAsync(action)
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Mangellisten kunne ikke gemmes.')
      return false
    }
  }

  return (
    <div>
      <div className="small" style={{ fontWeight: 600, marginBottom: 6 }}>
        Det mangler
      </div>

      <form
        className="row"
        style={{ gap: 8, flexWrap: 'wrap' }}
        onSubmit={(event) => {
          event.preventDefault()
          void run({ kind: 'add', text }).then((ok) => ok && setText(''))
        }}
      >
        <input
          maxLength={300}
          placeholder="F.eks. HPFI-afbryder, adgang til tavlerum"
          value={text}
          onChange={(e) => setText(e.target.value)}
          style={{ flex: '1 1 200px' }}
        />
        <button className="btn btn--sm" type="submit" disabled={change.isPending || !text.trim()}>
          Tilføj
        </button>
      </form>

      {error && <div className="tiny warn" style={{ marginTop: 6 }}>{error}</div>}
      {isLoading && <div className="tiny muted" style={{ marginTop: 8 }}>Henter ...</div>}
      {items && items.length === 0 && (
        <div className="tiny muted" style={{ marginTop: 8 }}>
          Intet mangler.
        </div>
      )}

      {items && items.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0' }} className="stack">
          {items.map((item) => (
            <MissingItemRow
              key={item.id}
              item={item}
              busy={change.isPending}
              onChange={(body) => run({ kind: 'update', itemId: item.id, body })}
              onDelete={() => {
                if (window.confirm(`Fjern "${item.text}" fra listen?`)) void run({ kind: 'delete', itemId: item.id })
              }}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function MissingItemRow({
  item,
  busy,
  onChange,
  onDelete
}: {
  item: TaskMissingItem
  busy: boolean
  onChange: (body: Record<string, unknown>) => Promise<boolean>
  onDelete: () => void
}) {
  // Bestillingen skrives, når punktet sættes til Bestilt - og kan rettes bagefter.
  const [ordering, setOrdering] = useState(false)
  const [orderNote, setOrderNote] = useState(item.orderNote ?? '')
  const [expected, setExpected] = useState(item.expectedDelivery ?? '')

  async function saveOrder(event: React.FormEvent) {
    event.preventDefault()
    const ok = await onChange({
      status: 'ordered',
      orderNote,
      ...(expected ? { expectedDelivery: expected } : { clearExpectedDelivery: true })
    })
    if (ok) setOrdering(false)
  }

  return (
    <li className="card card--flat" style={{ padding: 10 }}>
      <div className="row row--between" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div
            className="small"
            style={{ textDecoration: item.status === 'ready' ? 'line-through' : undefined, fontWeight: 600 }}
          >
            {item.text}
          </div>
          {item.status === 'ordered' && (item.orderNote || item.expectedDelivery) && (
            <div className="tiny" style={{ color: '#92400E' }}>
              Bestilt{item.orderNote ? `: ${item.orderNote}` : ''}
              {item.expectedDelivery ? ` · ventes ${formatDate(item.expectedDelivery)}` : ''}
            </div>
          )}
          {item.updatedBy && (
            <div className="tiny muted">
              {item.statusName} · {item.updatedBy.displayName}, {formatDateTime(item.updatedAt)}
            </div>
          )}
        </div>
        <span className="pill" style={statusStyle[item.status]}>
          {item.statusName}
        </span>
      </div>

      <div className="btn-group" style={{ marginTop: 8 }}>
        {item.status !== 'ordered' && (
          <button className="btn btn--sm" type="button" disabled={busy} onClick={() => setOrdering(true)}>
            Bestilt
          </button>
        )}
        {item.status === 'ordered' && !ordering && (
          <button className="btn btn--sm" type="button" disabled={busy} onClick={() => setOrdering(true)}>
            Ret bestilling
          </button>
        )}
        {item.status !== 'ready' && (
          <button className="btn btn--sm btn--primary" type="button" disabled={busy} onClick={() => void onChange({ status: 'ready' })}>
            Klar
          </button>
        )}
        {item.status !== 'missing' && (
          <button className="btn btn--sm" type="button" disabled={busy} onClick={() => void onChange({ status: 'missing' })}>
            Mangler igen
          </button>
        )}
        <button className="btn btn--sm" type="button" disabled={busy} onClick={onDelete}>
          Fjern
        </button>
      </div>

      {ordering && (
        <form className="stack" style={{ gap: 6, marginTop: 8 }} onSubmit={(e) => void saveOrder(e)}>
          <input
            autoFocus
            maxLength={500}
            placeholder="Hvad er bestilt, og hos hvem?"
            value={orderNote}
            onChange={(e) => setOrderNote(e.target.value)}
          />
          <label className="row tiny" style={{ gap: 6 }}>
            Ventes
            <input type="date" value={expected} onChange={(e) => setExpected(e.target.value)} style={{ width: 'auto' }} />
          </label>
          <div className="row" style={{ gap: 6 }}>
            <button className="btn btn--primary btn--sm" type="submit" disabled={busy}>
              Gem bestilling
            </button>
            <button className="btn btn--sm" type="button" onClick={() => setOrdering(false)}>
              Fortryd
            </button>
          </div>
        </form>
      )}
    </li>
  )
}
